import Profile3 from "../assets/img/team/profile-picture-3.jpg"

export const PayrollTypes = {
  hourly: 4,
  weekly: 1,
  biWeekly: 2,
  monthly: 3,
} as const
export const TransactionType = { percentage: 1, factor: 2, amount: 3 } as const

const colors = ["3f67fa"]

/**
 * Get random element from array
 */
export const getRandomArrayElement = <T>(arr: T[], cut = false): T => {
  const i = Math.floor(Math.random() * arr.length)
  if (cut && i in arr) {
    return arr.splice(i, 1)[0]
  }
  return arr[i]
}

/**
 * Generate user avatar placeholder
 */
export const userAvatar = (userName: string | null, size?: number): string => {
  if (userName === null) {
    return Profile3
  }

  const parts = userName.split(" ")
  let initials = ""
  parts.forEach(element => {
    if (initials.length < 2) {
      initials += element.substring(0, 1).toUpperCase()
    }
  })

  const colorToUse = getRandomArrayElement(colors)
  const finalSize = size ?? 75
  const path =
    "https://via.placeholder.com/" +
    finalSize +
    "/" +
    colorToUse +
    "/ffffff/?text=" +
    initials

  return path
}

/**
 * Check if text contains only HTML tags
 */
export const onlyHTMLTags = (text: string): boolean => {
  const holder = document.createElement("div")
  holder.innerHTML = text.trim()
  return holder.innerText.length === 0
}

/**
 * Returns true if the user is a platform super-admin.
 * Rule: email must belong to the @shearly.com domain.
 */
export const isSuperAdmin = (user: Record<string, any> | null): boolean => {
  if (!user) return false
  const email: string = (user.Email || user.email || "").toLowerCase()
  return email.endsWith("@shearly.com")
}

/**
 * Parse JWT token
 */
export const parseJwt = (
  token: string | null
): Record<string, unknown> | null => {
  try {
    if (!token) return null
    return JSON.parse(atob(token.split(".")[1]))
  } catch (e) {
    return null
  }
}

/**
 * Sort array by field
 */
export const sortArray = (
  records: Record<string, any>[],
  field: string,
  type: "number" | "string" = "string"
): Record<string, any>[] => {
  return records.sort((a, b) => {
    let nameA: any = null
    let nameB: any = null

    if (type === "number") {
      nameA = a[field]
      nameB = b[field]
    } else {
      nameA = a[field]?.toUpperCase?.() ?? ""
      nameB = b[field]?.toUpperCase?.() ?? ""
    }

    if (nameA < nameB) {
      return -1
    }
    if (nameA > nameB) {
      return 1
    }
    return 0
  })
}

/**
 * Format number as currency
 */
export const FormatMoney = (
  value: number,
  decimals = 2,
  decimalSeparator = ".",
  thousandsSeparator = ","
): string => {
  const sign = value < 0 ? "-" : ""
  const absValue = Math.abs(Number(value) || 0)
  const intPart = String(parseInt(absValue.toFixed(decimals)))
  let fractionalPart = intPart.length > 3 ? intPart.length % 3 : 0

  return (
    sign +
    (fractionalPart
      ? intPart.substr(0, fractionalPart) + thousandsSeparator
      : "") +
    intPart
      .substr(fractionalPart)
      .replace(/(\d{3})(?=\d)/g, "$1" + thousandsSeparator) +
    (decimals
      ? decimalSeparator +
        Math.abs(absValue - parseInt(intPart))
          .toFixed(decimals)
          .slice(2)
      : "")
  )
}

/**
 * Format number using Intl API
 */
export const NumberFormat = (value: number): string => {
  return new Intl.NumberFormat().format(isNaN(value * 1) ? 0 : value * 1)
}

/**
 * Generate random string ID
 */
export const makeId = (length: number): string => {
  let result = ""
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  const charactersLength = characters.length

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }

  return result
}

/**
 * Format bytes as human-readable text
 * @param bytes Number of bytes
 * @param si True to use metric (SI) units, false for binary (IEC)
 * @param dp Number of decimal places
 */
export const prettyFileSize = (bytes: number, si = false, dp = 1): string => {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + " B"
  }

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"]
  let u = -1
  const r = 10 ** dp

  let value = bytes
  do {
    value /= thresh
    ++u
  } while (
    Math.round(Math.abs(value) * r) / r >= thresh &&
    u < units.length - 1
  )

  return value.toFixed(dp) + " " + units[u]
}
